import {createPublisher} from 'src/publisher';
import {Helpers} from 'src/helpers';
import {
    hasRequiredProperties,
    mergeDeep,
    getPageUrl,
    getReferrer,
    getWindow,
    getResolutionHeight,
    getResolutionWidth,
    getViewportHeight,
    getViewportWidth,
} from './util';

import {
    REQUIRED_PAYLOAD_FIELDS,
    REQUIRED_EVENT_FIELDS,
    REQUIRED_EXPERIENCE_FIELDS,
    EXPERIENCE_TYPE_WEB,
    ERRORS,
} from './constants';
import {EventPacker} from 'eg-clickstream-event-experience-api-serde-js';
const NOOPCallbacks = {
    onFetchError: () => {},
    onPublishError: () => {},
    onInvalidPayload: () => {},
};
/**
 * @classdesc EgClickstream class to publish analytics events
 * @class
 */
class EgClickstream {
    constructor(callbacks = NOOPCallbacks, options = {}) {
        this.context = options.initialContext;
        this.publisher = createPublisher();
        this.callbacks = callbacks;
        this.unpacker = new EventPacker();
    }

    /**
     * Create a new instance from the EgClickstream class constructor
     *
     * @param {object} [callbacks] - (optional) contains callbacks to handle errors during egcs execution, it contains
     * @param {function} callbacks.onFetchError - callback to be executed when fetch fails.
     * @param {function} callbacks.onInvalidPayload - callback to be executed when payload sent to publish function is not valid.
     * @param {function} callbacks.onPublishError - callback to be executed when publish function throw any error.
     * @param {object} [options] - (optional) contains initialization values
     * @param {object} options.initialContext - initial values that will become part of the payload, overrides anything provided to #publish.
     * @returns {EgClickstream}
     *
     * @example const egc = egClickstream.getNewInstance();
     */
    getNewInstance(callbacks, options) {
        return new this.constructor(callbacks, options);
    }

    /**
     * Enriches data passed into this function, validates and sends it to the collect service
     *
     * @param {object} clientEvent - An object containing attributes to match the JSON schema specification defined for the event to be published. <br />
     * Example JSON schemas of virtual_agent_icon.viewed event:
     * <ul>
     * <li>[The latest version](https://eg-clickstream-event-manager-service-egdp-prod.us-east-1-vpc-018bd5207b3335f70.slb.egdp-prod.aws.away.black/api/v1/events/virtual_agent_icon.viewed)</li>
     * <li>[Version 3.3.0](https://eg-clickstream-event-manager-service-egdp-prod.us-east-1-vpc-018bd5207b3335f70.slb.egdp-prod.aws.away.black/api/v1/events/virtual_agent_icon.viewed?version=3.3.0)</li>
     * </ul>
     * @param {string} [serverPayload=''] - An optional JSON event payload from Experience API response. The clients don't need to understand or modify this. They should retrieve it from the Exp Api response and pass it to the SDK.
     * @param {*} - <strong>These attributes are commonly required by every event</strong>
     * @param {object} data.event - An event object, it contains
     * @param {string} data.event.event_version - (required) version of the event specification
     * @param {string} data.event.event_name - (required) name of the published event
     * @param {string} data.event.event_type - (required) type of the event
     *
     * @param {object} data.experience - Attributes related to the visual user experience.
     * @param {string} data.experience.page_name - (required) Allows us to understand where interaction events take place e.g. a date selection occurred on the Home Page.
     * @param {string} data.experience.app_name - (required) The application which rendered the HTML page. It should reflect the app's Github name.
     * @param {string} data.experience.app_version - (required) Version of the application.
     *
     * @param {*}  - <strong>These attributes will be enriched by EG Clickstream. It is not recommended for producing team to set this value.</strong>
     * @param {string} [data.experience.experience_platform] - (optional) Description of the technology platform, used to distinguish between versions of the same pageName during migration. If `window.eg_clickstream_overrides.experience_type` exists, EG Clickstream JS SDK will pass this value through.
     * @param {string} [data.experience.experience_type] - (optional) This will be enriched by EG Clickstream JS SDK. It is not recommended for producing team to set this value. If `window.eg_clickstream_overrides.experience_type` exists, EG Clickstream JS SDK will pass this value through, else set this value to "Web".
     * @param {number} [data.experience.viewport_width] - (optional) This will be enriched by EG Clickstream JS SDK. It is not recommended for producing team to set this value.
     * @param {number} [data.experience.viewport_height] - (optional) This will be enriched by EG Clickstream JS SDK. It is not recommended for producing team to set this value.
     * @param {number} [data.experience.resolution_width] - (optional) This will be enriched by EG Clickstream JS SDK. It is not recommended for producing team to set this value.
     * @param {number} [data.experience.resolution_height] - (optional) This will be enriched by EG Clickstream JS SDK. It is not recommended for producing team to set this value.
     *
     * @param {*} - <strong>For native app embedded WebView</strong>, EG Clickstream Native App SDK will populate the following global window object `window.eg_clickstream_overrides = {};` in an embedded WebView,
     *                 and EG Clickstream JS SDK will read and pass the values through to override.
     * @param {string} [data.identifiers.device_user_agent_id] - (optional) If window.eg_clickstream_overrides.device_user_agent_id exists, EG Clickstream JS SDK will pass this value through to override.
     * @param {string} [data.experience.experience_type] - (optional) This will be enriched by EG Clickstream JS SDK. It is not recommended for producing team to set this value. If `window.eg_clickstream_overrides.experience_type` exists, EG Clickstream JS SDK will pass this value through, else set this value to "Web".
     * @param {object} [data.device_information] - (optional) This will be enriched during the EG Clickstream Domain to Business Event enrichment process. These attributes may not be accessible/present in the Domain Event. It is not recommended for producing team to set this value.
     *                 For native app embedded WebView, EG Clickstream Native App SDK will populate the following global window object `window.eg_clickstream_overrides = {};` in an embedded WebView,
     *                 and EG Clickstream JS SDK will pass the values through to override. It contains
     * @param {string} [data.device_information.device_type] - (optional) If `window.eg_clickstream_overrides.device_type` exists, EG Clickstream JS SDK will pass this value through to override.
     * @param {string} [data.device_information.device_os] - (optional) If `window.eg_clickstream_overrides.device_os` exists, EG Clickstream JS SDK will pass this value through to override.
     * @param {string} [data.device_information.device_os_version] - (optional) If `window.eg_clickstream_overrides.device_os_version` exists, EG Clickstream JS SDK will pass this value through to override.
     * @param {string} [data.user.brand_customer_id] - (optional) If `window.eg_clickstream_overrides.brand_customer_id` exists, EG Clickstream JS SDK will pass this value through.
     * @example
     * const data = {
     *      event: {
     *          event_version: '1.0.0',
     *          event_name: 'virtual_agent_icon.viewed',
     *          event_type: 'Impression',
     *      },
     *      experience: {
     *          page_name: 'Homepage',
     *          app_name: 'vap-virtual-agent-control',
     *          app_version: '3.5.0',
     *      },
     *      virtual_agent: {
     *         conversation_id: '233445-234556'
     *      }
     * }
     *
     * egClickstream.instance.publish(clientEvent);
     *
     * @example <caption>with event tracking data from Experience API</caption>
     *
     * const data = {
     *      event: {
     *          event_version: '1.0.0',
     *          event_name: 'virtual_agent_icon.viewed',
     *          event_type: 'Impression',
     *      },
     *      experience: {
     *          page_name: 'Homepage',
     *          app_name: 'vap-virtual-agent-control',
     *          app_version: '3.5.0',
     *      },
     *      virtual_agent: {
     *         conversation_id: '233445-234556'
     *      }
     * }
     * // The serverPayload will come from Experience API response
     * serverPayload = "{metadata: {...}, events: {...}}"
     *
     * egClickstream.instance.publish(clientEvent, serverPayload);
     */
    publish(clientEvent, serverPayload = '') {
        const payload = this._createPayload(clientEvent, serverPayload);

        if (!this._isValidPayload(payload)) {
            this._logErrorWithEmit(
                new Error(
                    `publish failed due to invalid payload: ${JSON.stringify(
                        payload
                    )}`
                ),
                ERRORS.INVALID_PAYLOAD_ERROR
            );
            return;
        }
        try {
            this.publisher.publish(payload, this._logErrorWithEmit.bind(this));
        } catch (e) {
            this._logErrorWithEmit(e, ERRORS.PUBLISH_ERROR);
        }
    }

    /**
     * Updates `context` object by merging the new provided object with the existing.<br/>
     *
     * - Call `updateContext(data)` to set contextual data such as `identifiers.trace_id_of_the_view` when the value changes.
     * - EG Clickstream SDK will be enriching event data provided to `publish()` method with `context` object before sending it to EG Clickstream Collect service.<br/>
     * @param {object} data - An object containing attributes to be updated.
     */
    updateContext(data) {
        this.context = mergeDeep({}, this.context, data);
    }

    /**
     * Allows to log an error on the console and execute a callback based on the error type
     *
     * @private
     * @param {Error} error
     * @param {string} event
     */
    _logErrorWithEmit(error, event) {
        if (typeof this.callbacks[event] === 'function') {
            this.callbacks[event](error);
        }
        const logger = {
            error: (errorMsg) => {
                console.error(errorMsg);
            },
        };
        Helpers.logError(error, undefined, logger);
    }

    /**
     * Creates JSON payload from the provided data object<br/>
     *
     * @private
     * @param {object} clientEvent
     * @param {string} serverPayload
     * @returns {object}
     */
    _createPayload(clientEvent, serverPayload) {
        this._enrichContext();

        return mergeDeep(
            {},
            this._unpackServerPayload(serverPayload, clientEvent),
            clientEvent,
            this.context,
            this._getClickstreamOverrides()
        );
    }

    /**
     * Parse server JSON payload string and pick a major version from the server payload based on the event name and version provided in clientEvent
     * @param {string} serverPayload
     * @param {object} clientEvent
     * @returns Return the picked major payload as a JSON object
     */
    _unpackServerPayload(serverPayload, clientEvent) {
        let serverEventObj;
        const eventAttribute = clientEvent && clientEvent.event;
        const eventName = eventAttribute && eventAttribute.event_name;
        const eventVersion = eventAttribute && eventAttribute.event_version;

        if (!serverPayload || !eventName || !eventVersion) {
            return;
        }

        try {
            const eventFromServer = this.unpacker.unpack(serverPayload, eventName, eventVersion);

            // No matching event found from the provided server payload
            if (!eventFromServer) {
                this._logErrorWithEmit(new Error(`Unable to find a matching server payload for the provided [event_name]: ${eventName} \t [event_version]: ${eventVersion} \t [serverPayload]: ${serverPayload}`), ERRORS.INVALID_PAYLOAD_ERROR);
            }

            serverEventObj = JSON.parse(eventFromServer);
        } catch (error) {
            this._logErrorWithEmit(error, ERRORS.INVALID_PAYLOAD_ERROR);
        }

        return serverEventObj;
    }

    /**
     * Enriches `context` object with:
     * - `request` attributes
     * - `timestamps` attributes
     * - `experience` attributes
     *
     * @private
     */
    _enrichContext() {
        this.context = mergeDeep(
            {},
            this.context,
            this._getRequestContext(),
            this._getTimestamps(),
            this._getExperience()
        );
    }

    /**
     * Generates timestamps
     * @private
     * @returns object of timestamp
     */
    _getTimestamps() {
        return {
            timestamps: {
                event_origination_timestamp: Date.now(),
            },
        };
    }

    /**
     * Generates request context values
     * @private
     * @returns object of request context properties
     */
    _getRequestContext() {
        // The collect service does not have context on the request info of the parent view.
        // We want the request info to be captured in the JS SDK
        return {
            request: {
                request_url: getPageUrl(),
                referrer_url: getReferrer(),
            },
        };
    }

    /**
     * Generates experience object
     * @private
     * @returns object
     */
    _getExperience() {
        return {
            experience: {
                experience_type: EXPERIENCE_TYPE_WEB,
                viewport_width: getViewportWidth(),
                viewport_height: getViewportHeight(),
                resolution_width: getResolutionWidth(),
                resolution_height: getResolutionHeight(),
            },
        };
    }

    /**
     * Extracts data from global variable eg_clickstream_overrides and map it to the corresponding context properties
     * @private
     * @returns object of context properties
     */
    _getClickstreamOverrides() {
        const _window = getWindow();
        const overrides = _window.eg_clickstream_overrides;

        if (!overrides) {
            return {};
        }

        return {
            identifiers: {
                device_user_agent_id: overrides.device_user_agent_id,
            },
            device_information: {
                device_type: overrides.device_type,
                device_os: overrides.device_os,
                device_os_version: overrides.device_os_version,
            },
            experience: {
                experience_type: overrides.experience_type,
                experience_platform: overrides.experience_platform,
            },
            user: {
                brand_customer_id: overrides.brand_customer_id,
            },
        };
    }

    /**
     * Validates if payload contains all the required properties
     *
     * @private
     * @param {object} payload - JSON payload
     * @returns {boolean}
     */
    _isValidPayload(payload) {
        return (
            hasRequiredProperties(payload, ...REQUIRED_PAYLOAD_FIELDS) &&
            hasRequiredProperties(payload.event, ...REQUIRED_EVENT_FIELDS) &&
            hasRequiredProperties(
                payload.experience,
                ...REQUIRED_EXPERIENCE_FIELDS
            )
        );
    }
}

export const instance = new EgClickstream();
